import React, { useContext, useEffect, useState } from 'react';
import { getEmployeeAdvancesbranchwise } from '../../service/network';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Avatar,
  styled,
  Chip,
  useTheme,
  Skeleton,
  LinearProgress,
  TextField
} from '@mui/material';
import BarChartIcon from '@mui/icons-material/BarChart';
import {
  AttachMoney,
  Paid,
  ArrowUpward,
  ArrowDownward,
  RequestQuote,
  ShowChart,
  Business
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import CustomCard from '../../components/UIComponents/CustomCard';
import { AuthContext } from '../../context/auth.context';
import moment from 'moment';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';

const StatCard = styled(Card)(({ theme }) => ({
  borderRadius: '16px',
  boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)',
  background: theme.palette.background.paper,
  border: 'none',
  transition: 'all 0.3s ease',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 8px 25px 0 rgba(0,0,0,0.1)',
  }
}));

const StatAvatar = styled(Avatar)(({ theme, color }) => ({
  width: 48,
  height: 48,
  background: color ? `${color}15` : `${theme.palette.primary.main}15`,
  color: color || theme.palette.primary.main,
  borderRadius: '12px'
}));

const TrendChip = styled(Chip)(({ positive, theme }) => ({
  fontSize: '0.75rem',
  fontWeight: 700,
  padding: '0 8px',
  height: 24,
  backgroundColor: positive ? '#4caf5015' : '#f4433615',
  color: positive ? theme.palette.success.main : theme.palette.error.main,
  borderRadius: '8px',
  '& .MuiChip-icon': {
    fontSize: '14px',
    marginLeft: '4px'
  }
}));

const DashboardCard = styled(Card)(({ theme }) => ({
  borderRadius: '16px',
  boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)',
  height: '100%',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  '& .MuiCardContent-root': {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(3)
  }
}));

const AmountSummaryCards = ({ data, selLocation }) => {
  const theme = useTheme();

  const amountCards = [
    {
      title: 'Total Requested Amount',
      value: data.totalAdvanceAmount,
      icon: <RequestQuote sx={{ fontSize: 24 }} />,
      color: theme.palette.primary.main,
      trend: data.growthRate || 0,
      description: `${data.advanceCount} advances requested`
    },
    {
      title: 'Total Paid Amount',
      value: data.totalPaidAmount,
      icon: <Paid sx={{ fontSize: 24 }} />,
      color: theme.palette.success.main,
      trend: data.totalAdvanceAmount > 0 ?
        ((data.totalPaidAmount / data.totalAdvanceAmount) * 100).toFixed(1) : 0,
      description: `${data.paidCount} advances paid`
    },

    {
      title: 'Pending Amount',
      value: data.totalAdvanceAmount - data.totalPaidAmount,
      icon: <RequestQuote sx={{ fontSize: 24 }} />,
      color: theme.palette.warning.main,
      trend: data.totalAdvanceAmount > 0 ?
        (((data.totalAdvanceAmount - data.totalPaidAmount) / data.totalAdvanceAmount) * 100).toFixed(1) : 0,
      description: `${data.pendingCount} advances pending`
    }
  ];

  return (
    <Grid container spacing={3} sx={{ mb: 4 }}>
      {amountCards.map((card, index) => (
        <Grid item xs={12} sm={6} md={3} key={index} sx={{ display: 'flex' }}>
          <StatCard>
            <CardContent sx={{ p: 3, minWidth: 350 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <StatAvatar color={card.color}>
                  {card.icon}
                </StatAvatar>
                <TrendChip
                  positive={card.trend >= 0}
                  label={`${card.trend}%`}
                  icon={card.trend >= 0 ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />}
                />
              </Box>

              <Typography variant="h5" fontWeight="bold" gutterBottom>
                {card.value.toLocaleString('en-IN', {
                  style: 'currency',
                  currency: 'INR',
                  minimumFractionDigits: 0
                })}
              </Typography>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {card.title}
              </Typography>
              <Typography variant="bodyText1" color="primary.main" sx={{ mt: 'auto' }}>
                {card.description}
              </Typography>
            </CardContent>
          </StatCard>
        </Grid>
      ))}
    </Grid>
  );
};

const AmountComparisonChart = ({ data, theme, selLocation }) => {
  const chartData = [
    {
      name: 'Advance Requested',
      value: data.totalAdvanceAmount,
      color: theme.palette.info.main
    },
    {
      name: 'Amount Paid',
      value: data.totalPaidAmount,
      color: theme.palette.success.main
    },
    {
      name: 'Request Pending',
      value: data.totalAdvanceAmount - data.totalPaidAmount,
      color: theme.palette.primary.main
    }
  ];

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <BarChartIcon
          color="primary"
          sx={{
            mr: 1,
            transform: 'rotate(90deg)'
          }}
        />
        <Typography variant="h6">
          {`Disbursement Comparison for ${selLocation}`}
        </Typography>
      </Box>
      <Box sx={{ flex: 1, minHeight: 400 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{
              top: 20,
              right: 30,
              left: 40,
              bottom: 5,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
            <XAxis
              type="number"
              tickFormatter={(value) =>
                value.toLocaleString('en-IN', {
                  style: 'currency',
                  currency: 'INR',
                  maximumFractionDigits: 0
                }).replace('₹', '₹ ')
              }
            />
            <YAxis
              dataKey="name"
              type="category"
              width={120}
            />
            <Tooltip
              formatter={(value) =>
                [value.toLocaleString('en-IN', {
                  style: 'currency',
                  currency: 'INR'
                }), 'Amount']
              }
            />
            <Legend />
            <Bar dataKey="value" name="Amount (₹)">
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </>
  );
};

const BranchWiseAnalysis = ({ data, theme }) => {
  const branchData = data.reduce((acc, advance) => {
    const branch = advance.branch;
    const branchIndex = acc.findIndex(item => item.branch === branch);

    if (branchIndex >= 0) {
      acc[branchIndex].advance_amount += advance.advance_amount || 0;
      acc[branchIndex].paid_amount += advance.paid_amount || 0;
      // Calculate unpaid amount instead of claimed amount
      acc[branchIndex].unpaid_amount += (advance.advance_amount || 0) - (advance.paid_amount || 0);
    } else {
      acc.push({
        branch,
        advance_amount: advance.advance_amount || 0,
        paid_amount: advance.paid_amount || 0,
        // Calculate unpaid amount
        unpaid_amount: (advance.advance_amount || 0) - (advance.paid_amount || 0)
      });
    }
    return acc;
  }, []);

  // Custom tick formatter to handle long branch names
  const formatXAxisTick = (tickItem) => {
    // If branch name is too long, truncate it
    if (tickItem.length > 12) {
      return tickItem.substring(0, 10) + '...';
    }
    return tickItem;
  };

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Business color="primary" sx={{ mr: 1 }} />
        <Typography variant="h6">Branch-level Fund Analysis</Typography>
      </Box>
      <Box sx={{ flex: 1, minHeight: 400 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={branchData}
            margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 80, // Increased bottom margin to prevent label cutoff
            }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
            <XAxis 
              dataKey="branch" 
              interval={0}
              angle={-45}
              textAnchor="end"
              height={80}
              tickFormatter={formatXAxisTick}
            />
            <YAxis
              tickFormatter={(value) =>
                value.toLocaleString('en-IN', {
                  style: 'currency',
                  currency: 'INR',
                  maximumFractionDigits: 0
                }).replace('₹', '₹ ')
              }
            />
            <Tooltip
              formatter={(value, name) => {
                const formattedValue = value.toLocaleString('en-IN', {
                  style: 'currency',
                  currency: 'INR'
                });
                let displayName = name;
                if (name === 'advance_amount') displayName = 'Advance Requested';
                if (name === 'paid_amount') displayName = 'Amount Paid';
                if (name === 'unpaid_amount') displayName = 'Request Pending';
                
                return [formattedValue, displayName];
              }}
            />
            <Legend 
              wrapperStyle={{ 
                paddingTop: '20px',
                paddingBottom: '20px'
              }}
            />
            <Bar
              dataKey="advance_amount"
              name="Advance Requested"
              fill={theme.palette.info.main}
            />
            <Bar
              dataKey="paid_amount"
              name="Amount Paid"
              fill={theme.palette.success.main}
            />
            <Bar
              dataKey="unpaid_amount"
              name="Request Pending"
              fill={theme.palette.warning.main}
            />
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </>
  );
};

export default function EmployeeAdvanceDashboard() {
  const [employeeAdvances, setEmployeeAdvances] = useState([]);
  const [loading, setLoading] = useState(true);
  const { selLocation } = useContext(AuthContext);
  const [stats, setStats] = useState({
    totalAdvanceAmount: 0,
    totalPaidAmount: 0,
    totalClaimedAmount: 0,
    growthRate: 0,
    advanceCount: 0,
    paidCount: 0,
    claimedCount: 0,
    pendingCount: 0
  });
  const theme = useTheme();

  const [dateRange, setDateRange] = useState({
    fromDate: moment().startOf('month'),
    toDate: moment().endOf('month')
  });

  useEffect(() => {
    fetchAdvances();
  }, [selLocation, dateRange]);

  const fetchAdvances = async () => {
    setLoading(true);
    try {
      const payload = {
        doctype: "Employee Advance Branch Wise",
        report_name: "Employee Advance Branch Wise",
        filters: {
          from_date: dateRange.fromDate.format('YYYY-MM-DD'),
          to_date: dateRange.toDate.format('YYYY-MM-DD'),
          company: selLocation[0]
        },
        are_default_filters: false
      };

      const response = await getEmployeeAdvancesbranchwise(payload);
      const advances = response.data.message.result.slice(0, -1);
      const totalRow = response.data.message.result[response.data.message.result.length - 1];

      setEmployeeAdvances(advances);
      calculateStats(advances, totalRow);
    } catch (error) {
      console.error('Error fetching employee advances:', error);
    }
    setLoading(false);
  };

  const calculateStats = (advances, totalRow) => {
    const newStats = {
      totalAdvanceAmount: totalRow[5] || 0,
      totalPaidAmount: totalRow[6] || 0,
      totalClaimedAmount: totalRow[7] || 0,
      growthRate: 8.5, // This would need to be calculated based on previous period
      advanceCount: advances.filter(a => a.advance_amount > 0).length,
      paidCount: advances.filter(a => a.paid_amount > 0).length,
      claimedCount: advances.filter(a => a.claimed_amount > 0).length,
      pendingCount: advances.filter(a => (a.advance_amount - a.paid_amount) > 0).length
    };

    setStats(newStats);
  };

  const handleFromDateChange = (newValue) => {
    setDateRange(prev => ({
      ...prev,
      fromDate: newValue
    }));
  };

  const handleToDateChange = (newValue) => {
    setDateRange(prev => ({
      ...prev,
      toDate: newValue
    }));
  };

  return (
    <Box
      sx={{
        width: '100%',
        p: { xs: 1, sm: 2 },
        boxSizing: 'border-box'
      }}
    >
      <CustomCard
        bgColor="light"
        shadow="lg"
        borderRadius="xxl"
        sx={{
          width: '100%',
          minHeight: 800,
          overflow: 'hidden',
          display: 'flex',
          elevation:2,
          flexDirection: 'column',
          p: 3
          
        }}
      >
        <Box sx={{
          p: 4,
          background: theme.palette.primary.main,
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
          borderRadius: theme.borderRadius.xl
        }}>
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography variant="h4" gutterBottom fontWeight="bold">
                  Advance Disbursement Summary
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                  {selLocation ? `Showing data for ${selLocation}` : 'Showing all companies'}
                </Typography>
              </Box>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <DatePicker
                    label="From Date"
                    value={dateRange.fromDate}
                    onChange={handleFromDateChange}
                    maxDate={dateRange.toDate}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        sx={{
                          background: 'rgba(255,255,255,0.2)',
                          borderRadius: 1,
                          '& .MuiInputBase-input': {
                            color: 'white',
                            py: 1,

                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'transparent'
                          },
                          '& .MuiInputLabel-root': {
                            color: 'white'
                          },
                          '& .MuiSvgIcon-root': {
                            color: 'white'
                          }
                        }}
                      />
                    )}
                  />
                  <DatePicker
                    label="To Date"
                    value={dateRange.toDate}
                    onChange={handleToDateChange}
                    minDate={dateRange.fromDate}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        sx={{
                          background: 'rgba(255,255,255,0.2)',
                          borderRadius: 1,
                          '& .MuiInputBase-input': {
                            color: 'white',
                            py: 1
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'transparent'
                          },
                          '& .MuiInputLabel-root': {
                            color: 'white'
                          },
                          '& .MuiSvgIcon-root': {
                            color: 'white'
                          }
                        }}
                      />
                    )}
                  />
                </Box>
              </LocalizationProvider>
            </Box>
          </Box>
        </Box>

        <Box sx={{ p: 4, flex: 1, display: 'flex', flexDirection: 'column' }}>
          {loading ? (
            <>
              <LinearProgress sx={{ mb: 4 }} />
              <Grid container spacing={3}>
                {[1, 2, 3, 4].map((item) => (
                  <Grid item xs={12} sm={6} md={3} key={item}>
                    <Skeleton variant="rounded" height={180} />
                  </Grid>
                ))}
              </Grid>
              <Skeleton variant="rounded" sx={{ flex: 1, mt: 3, height: 400 }} />
            </>
          ) : (
            <>
              <AmountSummaryCards data={stats} />

              <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 2, display: 'flex', alignItems: 'center' }}>
                Advance Overview
              </Typography>

              <Grid container spacing={3} sx={{ flex: 1 }}>
                <Grid size={12}>
                  <DashboardCard>
                    <CardContent>
                      <AmountComparisonChart data={stats} theme={theme} selLocation={selLocation} />
                    </CardContent>
                  </DashboardCard>
                </Grid>
                <Grid size={12}>
                  <DashboardCard>
                    <CardContent>
                      <BranchWiseAnalysis data={employeeAdvances} theme={theme} />
                    </CardContent>
                  </DashboardCard>
                </Grid>
              </Grid>
            </>
          )}
        </Box>
      </CustomCard>
    </Box>
  );
}